// ActiveFilters.js — Renders applied filters using ResourceBadge styling
import React from 'react';
import { HelpCircle, X, XCircle, SlidersHorizontal } from 'lucide-react';
// Removed EnglishDigitIcon, MarathiDigitIcon as we'll use categoryIcons
import {
  categoryIcons,
  subjectTopicIcons,
  colorClasses,
  badgeBaseClass,
  marathiLabels
} from '../utils/categoryStyles';

// Step 1: Reuse same render logic from ResourceBadges
const renderBadgeWithClose = (key, icon, label, color, onRemove) => (
  <span
    key={key}
    className={`${badgeBaseClass} ${colorClasses[color]} flex items-center gap-1 truncate text-left transition-transform hover:scale-[1.02] focus:ring-2 focus:ring-offset-1 focus:ring-blue-500 focus:outline-none shadow-md hover:shadow-lg`}
    title={label}
    tabIndex={0}
    aria-label={label}
  >
    {icon} <span>{label}</span>
    <button
      onClick={onRemove}
      className="ml-1 text-gray-500 hover:text-red-600 focus:outline-none"
      title="Remove filter"
    >
      <X className="w-3 h-3" aria-hidden="true" />
    </button>
  </span>
);

// Step 2: Derive filter badge rendering from category + value
const renderFilterBadge = (category, value, subtopicSubjects = {}, onRemove) => {
  let icon = <HelpCircle className="w-4 h-4" aria-hidden="true" />;
  let color = 'gray';
  let label = value; // value is already lowercase from filters state
  let itemKey = value.replace(/\s+/g, '_'); // Default to underscore, e.g., "english_numerals"

  if (category === 'type') {
    // For 'type' category, keys in categoryIcons use hyphens
    itemKey = value.replace(/\s+/g, '-'); // e.g., "lesson plan" -> "lesson-plan"
    icon = categoryIcons.type.items[itemKey]?.icon || categoryIcons.type.items.default.icon || icon;
    color = categoryIcons.type.color || color;
    label = categoryIcons.type.items[itemKey]?.label || value.charAt(0).toUpperCase() + value.slice(1);
  } else if (category === 'difficulty') {
    // For 'difficulty', keys are simple lowercase, e.g., "easy"
    itemKey = value; // No replacement needed if keys are like "easy", "medium"
    icon = categoryIcons.difficulty.items[itemKey]?.icon || categoryIcons.difficulty.items.default.icon || icon;
    color = categoryIcons.difficulty.items[itemKey]?.color || categoryIcons.difficulty.color || color;
    label = categoryIcons.difficulty.items[itemKey]?.label || value.charAt(0).toUpperCase() + value.slice(1);
  } else if (category === 'subject') {
    // Assuming value is like "maths", "marathi"
    icon = categoryIcons.subject.items[itemKey]?.icon || categoryIcons.subject.items.default.icon || icon;
    color = 'green';
    if (value.toLowerCase() === 'marathi') {
      label = marathiLabels[value.toLowerCase()] || value;
    } else {
      label = value.charAt(0).toUpperCase() + value.slice(1);
    }
  } else if (category === 'subtopic') {
    const subject = subtopicSubjects[value] || ''; // value is subtopic name e.g. "number system"
    // const subjectKey = subject.toLowerCase(); // Not directly used for icon if subtopic icons are generic
    const subtopicKey = value.toLowerCase().replace(/\s+/g, '_');
    icon = categoryIcons.subtopic.items[subtopicKey]?.icon || categoryIcons.subtopic.items.default.icon || icon;
    color = 'green';

    if (subject.toLowerCase() === 'marathi') {
      label = marathiLabels[value.toLowerCase()] || value;
    } else {
      label = value.charAt(0).toUpperCase() + value.slice(1);
    }
  } else if (category === 'digits') { // Changed from 'language'
    icon = categoryIcons.digits.items[itemKey]?.icon || categoryIcons.digits.items.default.icon || icon;
    color = categoryIcons.digits.color || color;
    label = categoryIcons.digits.items[itemKey]?.label || value.charAt(0).toUpperCase() + value.slice(1);
  }

  return renderBadgeWithClose(`${category}-${value}`, icon, label, color, onRemove);
};

const ActiveFilters = ({ activeFilters, setFilters, subtopicSubjects = {}, title = 'Active Filters', noMargin = false }) => {
  const hasFilters = Object.entries(activeFilters || {}).some(([, vals]) => vals.length);

  if (!hasFilters) return null;

  return (
    <div className={`w-full bg-white border border-l-4 border-blue-300 border-gray-200 rounded-md shadow-sm px-2 sm:px-3 py-2${noMargin ? '' : ' mb-3'}`}>
      <div className="flex flex-row justify-between items-center flex-wrap gap-y-1 mb-2 border-b border-gray-100 pb-1">
        <div className="flex items-center gap-1 text-xs font-semibold text-gray-500">
          <span className="font-bold text-xs mr-1">{title}</span>
        </div>
        {title && (
          <button
            onClick={() =>
              setFilters({
                subject: [],
                subtopic: [],
                type: [],
                difficulty: [],
                digits: [],
              })
            }
            className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline active:scale-95 transition-transform"
          >
            Clear All
          </button>
        )}
      </div>

      <div
        className="flex flex-wrap gap-x-2 gap-y-1 text-sm max-h-[120px] overflow-y-scroll scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100"
        role="group"
        aria-label="Applied Filters"
      >
        {Object.entries(activeFilters).map(([category, values]) =>
          values.map((value) => (
            <React.Fragment key={`${category}-${value}`}>
              {renderFilterBadge(category, value, subtopicSubjects, () => {
                setFilters((prev) => ({
                  ...prev,
                  [category]: prev[category].filter((v) => v !== value),
                }));
              })}
            </React.Fragment>
          ))
        )}
      </div>
    </div>
  );
};

export default ActiveFilters;
